import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../common_ui_elements/ui_canvas_rect.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";

export class LeaveLevelDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 909;
  private static readonly DESIGN_H = 1395;
  private static readonly BG_Y = 343;
  private static readonly BG_H = 1052;
  private static readonly FRAME_Y = 674;

  private on_exit?: () => void;

  constructor( on_exit?: () => void )
  {
    const body_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( LeaveLevelDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.on_exit = on_exit;

    this.createBackground();
    this.createFrame();
    this.createTigerIcon();
    this.createTitle();
    this.createCloseButton();
    this.createActionButton();
    this.createHeartsBadge();
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( LeaveLevelDialog.BG_Y ),
      LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W ),
      LayoutScaler.heightFromDesign( LeaveLevelDialog.BG_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createFrame(): void
  {
    const design_frame_w = 777;
    const design_frame_h = 408;
    const design_frame_x = ( LeaveLevelDialog.DESIGN_W - design_frame_w ) / 2;
    const design_frame_y = LeaveLevelDialog.FRAME_Y;

    const frame_w = LayoutScaler.widthFromDesign( design_frame_w );
    const frame_h = LayoutScaler.heightFromDesign( design_frame_h );

    const frame = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_frame_x ),
      LayoutScaler.yFromDesign( design_frame_y ),
      frame_w,
      frame_h,
      ImgID.UI_PaternedBg
    );
    this.addChild( frame );

    const design_text_font_size = 70;
    const design_text_outline_w = 14;
    const design_text_outline_offset_y = 2;

    const MAGIC_NUM_CORRECTION = 0.7;
    const text_w = frame_w * MAGIC_NUM_CORRECTION;

    const design_text_offset_y = 50;

    const text = new UITextElement(
      this.ctx,
      ( frame_w - text_w ) / 2,
      LayoutScaler.yFromDesign( design_text_offset_y ),
      text_w,
      frame_h,
      {
        localization_key: "CLOSE_LEVEL_DIALOG.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_text_outline_w ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        outline_offset_y: LayoutScaler.yFromDesign( design_text_outline_offset_y ),
        auto_wrap: true,
      }
    );
    frame.addChild( text );
  }

  private createTigerIcon(): void
  {
    const design_img_w = 592;
    const design_img_h = 382;
    const design_img_x = ( LeaveLevelDialog.DESIGN_W - design_img_w ) / 2;
    const design_img_y = 0;

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_img_x ),
      LayoutScaler.yFromDesign( design_img_y ),
      LayoutScaler.widthFromDesign( design_img_w ),
      LayoutScaler.heightFromDesign( design_img_h ),
      ImgID.UI_TigerIcon
    );
    this.addChild( img );
  }

  private createHeartsBadge(): void
  {
    const design_outer_size = 216;
    const design_inner_size = 196;
    const design_heart_size = 174;
    const design_badge_y_offset = 90;
    const design_heart_y_offset = 7;

    const design_center_x = LeaveLevelDialog.DESIGN_W / 2;
    const design_outer_y = LeaveLevelDialog.FRAME_Y - design_badge_y_offset;

    const design_outer_x = design_center_x - design_outer_size / 2;
    const design_inner_x = design_center_x - design_inner_size / 2;
    const design_inner_y = design_outer_y + ( design_outer_size - design_inner_size ) / 2;
    const design_heart_x = design_center_x - design_heart_size / 2;
    const design_heart_y = design_outer_y + ( design_outer_size - design_heart_size ) / 2 + design_heart_y_offset;

    const outer_circle = new UICanvasRect(
      this.ctx,
      LayoutScaler.xFromDesign( design_outer_x ),
      LayoutScaler.yFromDesign( design_outer_y ),
      LayoutScaler.widthFromDesign( design_outer_size ),
      LayoutScaler.heightFromDesign( design_outer_size ),
      ColorScheme.white,
      LayoutScaler.widthFromDesign( design_outer_size / 2 )
    );
    this.addChild( outer_circle );

    const inner_circle = new UICanvasRect(
      this.ctx,
      LayoutScaler.xFromDesign( design_inner_x ),
      LayoutScaler.yFromDesign( design_inner_y ),
      LayoutScaler.widthFromDesign( design_inner_size ),
      LayoutScaler.heightFromDesign( design_inner_size ),
      ColorScheme.more_lives_badge_circle,
      LayoutScaler.widthFromDesign( design_inner_size / 2 )
    );
    this.addChild( inner_circle );

    const heart_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_heart_x ),
      LayoutScaler.yFromDesign( design_heart_y ),
      LayoutScaler.widthFromDesign( design_heart_size ),
      LayoutScaler.heightFromDesign( design_heart_size ),
      ImgID.UI_CrushedHeart
    );
    this.addChild( heart_icon );
  }

  private createTitle(): void
  {
    const design_title_y = 442;
    const design_title_h = 99;
    const design_font_size = 70;
    const design_outline_w = 20;

    const title_full_w = LayoutScaler.widthFromDesign( LeaveLevelDialog.DESIGN_W );
    const MAGIC_NUM_CORRECTION = 0.8;
    const title_w = title_full_w * MAGIC_NUM_CORRECTION;

    const title = new UITextElement(
      this.ctx,
      ( title_full_w - title_w ) / 2,
      LayoutScaler.yFromDesign( design_title_y ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "LEAVE_LEVEL.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    this.addChild( title );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_x = LeaveLevelDialog.DESIGN_W - 80;
    const design_btn_y = 310;

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      LayoutScaler.widthFromDesign( design_btn_size ),
      LayoutScaler.heightFromDesign( design_btn_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => this.close() );
    this.addChild( btn );
  }

  private createActionButton(): void
  {
    const design_btn_w = 570;
    const design_btn_h = 250;
    const design_btn_y = 1105;
    const design_btn_x = ( LeaveLevelDialog.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_RedBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const design_font_size = 60;

    const text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        outline_color: ColorScheme.red_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    btn.addChild( text, true );

    btn.setOnClick( () =>
    {
      this.close();
      this.on_exit?.();
    } );
    this.addChild( btn );
  }

  public static open( on_exit?: () => void ): LeaveLevelDialog
  {
    return DialogManager.openPooled( "LeaveLevelDialog", { on_exit }, () => new LeaveLevelDialog( on_exit ) ) as LeaveLevelDialog;
  }

  public override isPoolable(): boolean { return true; }

  public override onReuse( params: { on_exit?: () => void } ): void
  {
    this.on_exit = params.on_exit;
  }

  public name(): string
  {
    return "LeaveLevelDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
